import React, { useEffect, useState } from 'react';
import { api } from '../services/api';
import Select from './Select';
import '../styles/forms.css';
import '../styles/ManageAccounts.css';
import Icon from '@mdi/react';
import { mdiPlus, mdiSquareEditOutline, mdiDeleteOutline } from '@mdi/js';

const ACCOUNT_TYPES = [
    { value: 'Checking', label: 'Checking' },
    { value: 'Term', label: 'Term' },
    { value: 'Credit Card', label: 'Credit Card' },
    { value: 'Investment', label: 'Investment' },
];

const CURRENCIES = [
    { value: 'EUR', label: 'EUR' },
    { value: 'USD', label: 'USD' },
    { value: 'GBP', label: 'GBP' },
    { value: 'CHF', label: 'CHF' },
    { value: 'JPY', label: 'JPY' },
];

const emptyForm = () => ({
    name: '',
    type: 'Checking',
    currency: 'EUR',
    is_open: true,
    apy_tracked: false,
    initial_balance: 0,
    opened_on: new Date().toISOString().substring(0, 10),
});

const COLUMNS = ['name', 'currency', 'initial_balance', 'opened_on', 'is_open'];

const compareAccounts = (a, b, field) => {
    if (field === 'initial_balance') return Number(a.initial_balance) - Number(b.initial_balance);
    if (field === 'is_open') return (a.is_open === b.is_open) ? 0 : a.is_open ? -1 : 1;
    if (field === 'opened_on') return (a.opened_on || '').localeCompare(b.opened_on || '');
    return (a[field] || '').localeCompare(b[field] || '');
};

const GroupedAccountTable = ({ type, accounts, onEdit, onDelete }) => {
    const [sortField, setSortField] = useState('name');
    const [sortDir, setSortDir] = useState('asc');

    const handleSort = (field) => {
        if (!COLUMNS.includes(field)) return;
        if (field === sortField) {
            setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir('asc');
        }
    };

    const sorted = [...accounts].sort((a, b) => {
        const cmp = compareAccounts(a, b, sortField);
        return sortDir === 'asc' ? cmp : -cmp;
    });

    const arrow = (field) => sortField === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

    return (
        <div className="account-group">
            <h3 className="account-group-title">{type} <span className="account-group-count">({accounts.length})</span></h3>
            <div className="table-wrapper">
                <table>
                    <thead>
                        <tr>
                            <th className="sortable" onClick={() => handleSort('name')}>Name{arrow('name')}</th>
                            <th className="sortable" onClick={() => handleSort('currency')}>Currency{arrow('currency')}</th>
                            <th className="sortable num" onClick={() => handleSort('initial_balance')}>Initial Balance{arrow('initial_balance')}</th>
                            <th className="sortable" onClick={() => handleSort('opened_on')}>Opened On{arrow('opened_on')}</th>
                            <th className="sortable" onClick={() => handleSort('is_open')}>Status{arrow('is_open')}</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((account) => (
                            <tr key={account.id}>
                                <td>{account.name}</td>
                                <td>{account.currency || '—'}</td>
                                <td className="num">{Number(account.initial_balance).toFixed(2)}</td>
                                <td>{(account.opened_on || '').substring(0, 10)}</td>
                                <td>
                                    <span className={`status-badge ${account.is_open ? 'open' : 'closed'}`}>
                                        {account.is_open ? 'Open' : 'Closed'}
                                    </span>
                                </td>
                                <td>
                                    <button
                                        className="btn btn--ghost"
                                        onClick={() => onEdit(account)}
                                        title="Edit"
                                    >
                                        <Icon path={mdiSquareEditOutline} size={0.75} />
                                    </button>
                                    <button
                                        className="btn btn--ghost"
                                        onClick={() => onDelete(account.id)}
                                        title="Delete"
                                    >
                                        <Icon path={mdiDeleteOutline} size={0.75} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const ManageAccounts = () => {
    const [accounts, setAccounts] = useState([]);
    const [editing, setEditing] = useState(null);
    const [formData, setFormData] = useState(emptyForm());
    const [panelOpen, setPanelOpen] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchAccounts();
    }, []);

    const fetchAccounts = async () => {
        try {
            const response = await api.get('/accounts');
            setAccounts(response.data);
        } catch (err) {
            console.error('Error fetching accounts:', err);
            setError('Failed to load accounts.');
        }
    };

    const openCreatePanel = () => {
        setEditing(null);
        setFormData(emptyForm());
        setError('');
        setPanelOpen(true);
    };

    const openEditPanel = (account) => {
        setEditing(account);
        setFormData({
            name: account.name,
            type: account.type,
            currency: account.currency || 'EUR',
            is_open: account.is_open,
            apy_tracked: !!account.apy_tracked,
            initial_balance: account.initial_balance,
            opened_on: (account.opened_on || '').substring(0, 10),
        });
        setError('');
        setPanelOpen(true);
    };

    const closePanel = () => {
        setPanelOpen(false);
        setEditing(null);
        setError('');
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSelect = (field, option) => {
        setFormData(prev => ({ ...prev, [field]: option ? option.value : null }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.name.trim()) {
            setError('Account name is required.');
            return;
        }

        try {
            if (editing) {
                await api.patch(`/accounts/${editing.id}`, formData);
            } else {
                await api.post('/accounts', formData);
            }
            closePanel();
            await fetchAccounts();
        } catch (err) {
            setError('Failed to save account: ' + (err.response?.data?.error || err.message));
        }
    };

    const handleDelete = async (accountId) => {
        if (!window.confirm('Are you sure you want to delete this account? Transactions will be kept but the account will be hidden.')) {
            return;
        }
        try {
            await api.delete(`/accounts/${accountId}`);
            await fetchAccounts();
        } catch (err) {
            console.error('Error deleting account:', err);
            setError('Failed to delete account.');
        }
    };

    const panelTitle = editing ? 'Edit Account' : 'New Account';

    const GROUP_ORDER = ['Checking', 'Cash', 'Credit Card', 'Loan', 'Term', 'Investment', 'Shares', 'Assets'];

    const groupedAccounts = accounts.reduce((groups, account) => {
        const type = account.type || 'Other';
        (groups[type] = groups[type] || []).push(account);
        return groups;
    }, {});

    const sortedGroups = Object.entries(groupedAccounts).sort(([a], [b]) => {
        const ai = GROUP_ORDER.indexOf(a);
        const bi = GROUP_ORDER.indexOf(b);
        if (ai === -1 && bi === -1) return a.localeCompare(b);
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
    });

    return (
        <div className="card">
            <div className="transaction-table accounts-table">
                <h2 className="page-title">Manage Accounts</h2>

                <div className="header">
                    <div className="buttons-bar">
                        <button
                            className="btn btn--primary"
                            onClick={openCreatePanel}
                            type="button"
                            data-tour="new-account"
                        >
                            <Icon path={mdiPlus} size={0.8} /> New Account
                        </button>
                    </div>
                </div>

                {accounts.length === 0 ? (
                    <div className="table-wrapper">
                        <table>
                            <tbody>
                                <tr><td className="empty-row">No accounts yet. Click "New Account" to create one.</td></tr>
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="accounts-groups">
                        {sortedGroups.map(([type, groupAccounts]) => (
                            <GroupedAccountTable
                                key={type}
                                type={type}
                                accounts={groupAccounts}
                                onEdit={openEditPanel}
                                onDelete={handleDelete}
                            />
                        ))}
                    </div>
                )}

                {panelOpen && (
                    <div className="edit-panel create-panel" data-tour="account-form-panel">
                        <div className="panel-header">
                            <h3>{panelTitle}</h3>
                            <button onClick={closePanel}>×</button>
                        </div>

                        <form onSubmit={handleSubmit} className="form">
                            {error && <div className="error">{error}</div>}

                            <div className="form-group">
                                <label>Name
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Enter account name"
                                        required
                                    />
                                </label>
                            </div>

                            <div className="form-group-row">
                                <div className="form-group">
                                    <label>Type
                                        <Select
                                            options={ACCOUNT_TYPES}
                                            onSelect={(option) => handleSelect('type', option)}
                                            value={formData.type}
                                        />
                                    </label>
                                </div>
                                <div className="form-group">
                                    <label>Currency
                                        <Select
                                            options={CURRENCIES}
                                            onSelect={(option) => handleSelect('currency', option)}
                                            value={formData.currency}
                                        />
                                    </label>
                                </div>
                            </div>

                            <div className="form-group-row">
                                <div className="form-group">
                                    <label>Initial Balance
                                        <input
                                            type="number"
                                            step="0.01"
                                            name="initial_balance"
                                            value={formData.initial_balance}
                                            onChange={handleChange}
                                            required
                                        />
                                    </label>
                                </div>
                                <div className="form-group">
                                    <label>Opened On
                                        <input
                                            type="date"
                                            name="opened_on"
                                            value={formData.opened_on}
                                            onChange={handleChange}
                                            required
                                        />
                                    </label>
                                </div>
                            </div>

                            <div className="form-group">
                                <label className="checkbox-label">
                                    <input
                                        type="checkbox"
                                        name="is_open"
                                        checked={formData.is_open}
                                        onChange={handleChange}
                                    />
                                    <span className="checkbox-custom" />
                                    <span className="checkbox-text">Account is open</span>
                                </label>
                            </div>

                            {formData.type === 'Term' && (
                                <div className="form-group">
                                    <label className="checkbox-label">
                                        <input
                                            type="checkbox"
                                            name="apy_tracked"
                                            checked={formData.apy_tracked}
                                            onChange={handleChange}
                                        />
                                        <span className="checkbox-custom" />
                                        <span className="checkbox-text">
                                            Track APY (show on the Investments page)
                                        </span>
                                    </label>
                                </div>
                            )}

                            <div className="form-buttons">
                                <button type="button" className="btn btn--secondary" onClick={closePanel}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn--primary">
                                    {editing ? 'Save Changes' : 'Create Account'}
                                </button>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ManageAccounts;